import React, { useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { Users, Dumbbell, ClipboardList, Mail, LogOut, Menu, X, ShoppingBag, CalendarDays, Zap } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const LOGO = "https://customer-assets.emergentagent.com/job_261f6229-25c6-462a-bf7c-d1c15ef074eb/artifacts/vpvrvwp5_logo%20imagotip%20xpro%20blanco.png";

const NAV = [
  { to: "socios",     icon: Users,           label: "Socios" },
  { to: "maquinas",   icon: Dumbbell,        label: "Màquines" },
  { to: "ejercicios", icon: Zap,             label: "Ejercicios" },
  { to: "rutinas",    icon: ClipboardList,   label: "Rutinas" },
  { to: "productos",  icon: ShoppingBag,     label: "Tienda" },
  { to: "reservas",   icon: CalendarDays,    label: "Reservas" },
  { to: "emails",     icon: Mail,            label: "Emails" },
];

export default function AdminLayout() {
  const { admin, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!checked) return <div className="min-h-screen bg-black" />;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 text-[11px] font-black tracking-[0.18em] uppercase transition-all duration-150 ${
      isActive
        ? "border-l-2 border-[#FFCC00] text-[#FFCC00] bg-[#FFCC00]/5 pl-[14px]"
        : "border-l-2 border-transparent text-white/45 hover:text-white/80 hover:bg-white/4"
    }`;

  return (
    <div className="flex h-screen bg-[#080808] text-white overflow-hidden" data-testid="admin-layout">

      {/* ——— Desktop Sidebar ——— */}
      <aside className="hidden lg:flex flex-col w-52 shrink-0 border-r border-white/8 bg-[#060606]">
        <div className="px-5 py-5 border-b border-white/8 flex items-center gap-3">
          <img src={LOGO} alt="XPRO" className="h-7 w-auto" />
          <div>
            <p className="text-[10px] font-black tracking-[0.25em] text-[#FFCC00] uppercase leading-none">Xpro</p>
            <p className="text-[9px] text-white/30 tracking-[0.2em] uppercase">Admin</p>
          </div>
        </div>

        <nav className="flex-1 py-4 space-y-0.5">
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} className={linkClass} data-testid={`nav-${to}`}>
              <Icon size={15} /> {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/8 px-4 py-4">
          <p className="text-[10px] text-white/30 truncate mb-3">{admin.email}</p>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-[10px] font-bold tracking-[0.18em] uppercase text-white/40 hover:text-white transition-colors"
          >
            <LogOut size={13} /> Sortir
          </button>
        </div>
      </aside>

      {/* ——— Mobile Top Bar ——— */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-[#060606] border-b border-white/8 h-12 flex items-center justify-between px-4">
        <img src={LOGO} alt="XPRO" className="h-6 w-auto" />
        <button onClick={() => setMobileOpen((v) => !v)} className="text-white/60 hover:text-white">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* ——— Mobile Drawer ——— */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-20 flex" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative w-52 bg-[#060606] border-r border-white/8 flex flex-col pt-12" onClick={(e) => e.stopPropagation()}>
            <nav className="flex-1 py-4 space-y-0.5">
              {NAV.map(({ to, icon: Icon, label }) => (
                <NavLink key={to} to={to} className={linkClass} onClick={() => setMobileOpen(false)} data-testid={`nav-mobile-${to}`}>
                  <Icon size={15} /> {label}
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-white/8 px-4 py-4">
              <button onClick={handleLogout} className="flex items-center gap-2 text-[10px] font-bold tracking-[0.18em] uppercase text-white/40 hover:text-white transition-colors">
                <LogOut size={13} /> Sortir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Content ——— */}
      <main className="flex-1 overflow-y-auto lg:pt-0 pt-12">
        <Outlet />
      </main>
    </div>
  );
}
